import { BaseService } from './base-service';

let _local_banned_airports = {};
export class MediaService extends BaseService {
  private _airportInfosCache: { [key: string]: any } = {};

  public async getAirportInfos(iataCode: string, format: string): Promise<any> {
    const _UnknownAirport = {
      airportName: 'Unknown',
      cityName: 'Unknown',
      cityCode: 'UNK',
      countryCode: 'UNK',
      airportCode: iataCode || 'UNK',
      countryName: 'Unknown',
      imageUrl: ''
    };
    
    let cacheKey = `${iataCode}-${format}`;
    
    if (this._airportInfosCache[cacheKey]) {
      return this._airportInfosCache[cacheKey];
    }
    
    let cacheData = localStorage.getItem('airportInfosCache');
    if (cacheData && cacheData.length > 1024 * 1024 * 2) {
      console.debug('Airport cache is too large, clearing...');
      localStorage.removeItem('airportInfosCache');
      cacheData = null;
    }
    
    const cachedInfo = JSON.parse(cacheData || '{}') || {};
    if (cachedInfo[cacheKey]) {
      this._airportInfosCache = cachedInfo;
      return cachedInfo[cacheKey];
    }

    if (_local_banned_airports[iataCode]) {
      console.debug('Airport is banned:', iataCode);
      return _UnknownAirport;
    }
    
    try {
      let result = await this.callHeyApiTech('GET', `/v3/media/by-airport?airport=${iataCode}&format=${format}`, null);
      
      if (!result.success) {
        console.error('Failed to fetch airport infos:', result.error);
        _local_banned_airports[iataCode] = true;
        return _UnknownAirport;
      }
      
      let rawReply = result.data;
      if (!rawReply?.files?.format?.[format]?.[0]) {
        console.error('Format not found in reply:', format);
        return _UnknownAirport;
      }

      let rawFormat = rawReply.files.format[format][0];
      console.debug(`Airport infos for ${iataCode} fetched:`, rawFormat);
      
      let airportInfos = {
        airportName: rawFormat.airportName,
        cityName: rawFormat.cityName,
        cityCode: rawFormat.cityCode,
        countryCode: rawFormat.countryCode,
        airportCode: rawFormat.airportCode,
        countryName: rawFormat.countryName,
        imageUrl: rawFormat.url[0]
      };
      
      this._airportInfosCache[cacheKey] = airportInfos;
      cachedInfo[cacheKey] = airportInfos;
      try {
        localStorage.setItem('airportInfosCache', JSON.stringify(cachedInfo));
      } catch (error) {
        console.error('Error saving airport info to localStorage:', error);
        localStorage.clear();
      }
      
      return airportInfos;
    } catch (error) {
      console.error(`Error fetching airport infos for ${iataCode}:`, error);
      return _UnknownAirport;
    }
  }

  public async getFlightDetails(carrierCode: string, flightNumber: string, departureDate: string): Promise<any> {
    try {
      const cacheKey = `${carrierCode}-${flightNumber}-${departureDate}`;
      
      let cacheData = localStorage.getItem('flightDetailsCache');
      if (cacheData && cacheData.length > 1024 * 1024 * 2) {
        console.debug('Flight details cache is too large, clearing...');
        localStorage.removeItem('flightDetailsCache');
        cacheData = null;
      }
      
      const flightDetailsCache = JSON.parse(cacheData) || {};
      
      if (flightDetailsCache[cacheKey]) {
        return flightDetailsCache[cacheKey];
      }
      
      console.debug('Fetching flight details:', { carrierCode, flightNumber, departureDate });
      
      const result = await this.callHeyApiUser(
        'GET',
        `/v3/air-products?carrierCode=${carrierCode}&flightNumber=${flightNumber}&departureDate=${departureDate}`,
        null
      );

      if (!result.success) {
        console.error('Failed to fetch flight details:', result.error);
        return null;
      }

      flightDetailsCache[cacheKey] = result.data;
      try {
        localStorage.setItem('flightDetailsCache', JSON.stringify(flightDetailsCache));
      } catch (error) {
        console.error('Error saving flight details to localStorage:', error);
        localStorage.clear();
      }
      return result.data;
    } catch (error) {
      console.error('Error fetching flight details:', error);
      return null;
    }
  }

  public async getWeatherByAirportCode(airportCode: string): Promise<any> {
    console.debug('================================================');
    console.debug('🌤️ GETTING WEATHER DATA BY AIRPORT CODE');
    console.debug('📍 Airport code:', airportCode);
    console.debug('================================================');

    if (!this.heyUtils.techToken) {
      console.debug('Getting technical token first...');
      await this.getTechnicalToken();
    }

    console.debug('🔑 Tech token available:', !!this.heyUtils.techToken);
    console.debug('🔑 Tech token prefix:', this.heyUtils.techToken?.substring(0, 10) + '...');
    
    const endpoint = `/v3/weather/airport/${airportCode}`;
    console.debug('🌐 Calling endpoint:', endpoint);
    
    try {
      const result = await this.callHeyApi(
        'GET', 
        endpoint, 
        this.heyUtils.techToken,
        null
      );
      
      console.debug('================================================');
      console.debug('📥 WEATHER API RESPONSE');
      console.debug('📊 Success:', result.success);
      console.debug('📊 Status code:', result.status);
      
      if (!result.success) {
        console.error('❌ Error details:', result.error);
        console.error('❌ Response data:', JSON.stringify(result.data, null, 2));
      } else {
        console.debug('✅ Response data summary:', Object.keys(result.data));
        console.debug('✅ Has Headline:', !!result.data?.Headline);
        console.debug('✅ Has DailyForecasts:', !!result.data?.DailyForecasts);
        console.debug('✅ Number of forecasts:', result.data?.DailyForecasts?.length);
      }
      console.debug('================================================');

      if (!result.success) {
        throw new Error('Failed to get weather data: ' + result.error);
      }
      
      return result.data;
    } catch (error) {
      console.error('❌❌❌ Exception thrown in getWeatherByAirportCode:', error);
      throw error;
    }
  }

  public async getWeatherData(latitude: number, longitude: number): Promise<any> {
    console.debug('================================================');
    console.debug('🌤️ GETTING WEATHER DATA');
    console.debug('📍 Coordinates:', { latitude, longitude });
    console.debug('================================================');

    if (!this.heyUtils.techToken) {
      console.debug('Getting technical token first...');
      await this.getTechnicalToken();
    }

    const result = await this.callHeyApi(
      'GET', 
      `/v3/contextualisation/weather/activity?latitude=${latitude}&longitude=${longitude}`, 
      this.heyUtils.techToken,
      null
    );
    
    console.debug('================================================');
    console.debug('📥 WEATHER API RESPONSE');
    console.debug('📊 Result:', result);
    console.debug('================================================');

    if (!result.success) {
      throw new Error('Failed to get weather data: ' + result.error);
    }
    
    return result.data;
  }

  private buildCacheKeyForContextualisation(tripId: string, filters: { interests?:string,  nbResults?: number,  radius?: string; minPrice?: number; maxPrice?: number; category?: string; location?: { name: string, iata:string }, date:string, carrierCode:string, flightIdentifier:string }, catalog: string): string {
    let cacheKey = `${tripId}-${catalog}`;
    
    if (filters) {
      if (filters.radius) {
        cacheKey += `-radius-${filters.radius}`;
      }
      if (filters.minPrice) {
        cacheKey += `-minPrice-${filters.minPrice}`;
      }
      if (filters.maxPrice) {
        cacheKey += `-maxPrice-${filters.maxPrice}`;
      }
      if (filters.category) {
        cacheKey += `-category-${filters.category}`;
      }
      if (filters.location && filters.location.name) {
        cacheKey += `-location-${filters.location.name}`;
      }
      if (filters.location && filters.location.iata) {
        cacheKey += `-locationIATA-${filters.location.iata}`;
      }
      if (filters.interests) {
        cacheKey += `-interests-${filters.interests}`;
      }
      if (filters.nbResults) {
        cacheKey += `-nbResults-${filters.nbResults}`;
      }
      if (filters.carrierCode) {
        cacheKey += `-carrierCode-${filters.carrierCode}`;
      }
      if (filters.flightIdentifier) {
        cacheKey += `-flightIdentifier-${filters.flightIdentifier}`;
      }
      if (filters.date) {
        cacheKey += `-date-${filters.date}`;
      }
    }
    
    return cacheKey;
  }

  public async getActivities(tripId: string, filters?: { interests?:string, nbResults?: number, radius?: string; minPrice?: number; maxPrice?: number; category?: string; location?: { name: string, iata:string }, date:string, carrierCode:string, flightIdentifier:string }, catalog: string = "discover"): Promise<any> {
    console.debug(`🎯 Getting ${catalog} for trip:`, tripId);

    let cacheKey = this.buildCacheKeyForContextualisation(tripId, filters, catalog);
    let ctx_cache = {}
    let cacheData = localStorage.getItem(`contextualisationCacheList`);
    if (cacheData) {
      console.debug(`📦 Cache hit for ${catalog}:`, cacheKey)
      ctx_cache = JSON.parse(cacheData);
      if (ctx_cache[cacheKey]) {
        console.debug(`📦 Cache hit for ${catalog}:`, ctx_cache[cacheKey]);
        return ctx_cache[cacheKey].reply;
      }
      console.debug(`📦 Cache miss for ${catalog}:`, cacheKey);
    }
    if (tripId == "inspiration-placeholder") {
      tripId = "";
    }
    const response = await this.callHeyApiUser('POST', '/v3/contextualisation/contextualise', {
      version: 1,
      catalog: catalog,
      tripReference: tripId,
      travellerId: this.heyUtils.user_id,
      maxResults: filters?.nbResults || 10,
      additionalInputs: {
        radius: filters?.radius || "",
        minPrice: filters?.minPrice || null,
        maxPrice: filters?.maxPrice || null,
        category: filters?.category,
        location: filters?.location,
        interests: filters?.interests,
        date: filters?.date || "",
        carrierCode: filters?.carrierCode || "",
        flightIdentifier: filters?.flightIdentifier || ""
      }
    });

    if (!response.success) {
      console.error(`Failed to fetch ${catalog}:`, response.error);
      throw new Error(`Failed to fetch ${catalog}: ${response.error}`);
    }

    console.debug(`📦 ${catalog} response:`, response.data);

    let reply = {
      trackingId: response.data.trackingId,
      results: (response.data.results || []).map(activity => ({
        id: activity.id || '',
        name: activity.name || '',
        url: activity.url || '',
        description: activity.description || '',
        categories: activity.categories || [],
        thumbnailUrl: activity.thumbnailUrl || '',
        imageUrls: activity.imageUrls || [],
        minPrice: activity.minPrice || 0,
        maxPrice: activity.maxPrice || 0,
        numberOfRatings: activity.numberOfRatings || 0,
        ratingAverage: activity.ratingAverage || 0,
        currency: activity.currency
      }))
    };

    console.debug(`📦 ${catalog} reply:`, reply)
    ctx_cache[cacheKey] = {
      reply: reply,
      timestamp: Date.now()
    };
    // Keep only 8 latests search in cache
    let keys = Object.keys(ctx_cache);
    if (keys.length > 8) {
      keys.sort((a, b) => ctx_cache[b].timestamp - ctx_cache[a].timestamp);
      for (let i = 8; i < keys.length; i++) {
        delete ctx_cache[keys[i]];
      }
    }
    try {
      localStorage.setItem(`contextualisationCacheList`, JSON.stringify(ctx_cache));
    } catch (error) {
      console.error('Error saving contextualisation cache to localStorage:', error);
      localStorage.clear();
    }
    console.debug(`📦 ${catalog} cache updated:`, ctx_cache);
    
    return reply;
  }
}
